import { sendToApi } from 'api/client'
import { AIJobStatus } from 'types'
import { StoreSnapshot } from '@tldraw/tldraw'

export enum ChatMessageRole {
  User = 'user',
  Assistant = 'assistant',
}

export interface ChatTextContent {
  type: 'text'
  text: string
}

export interface ChatImageContent {
  type: 'image'
  image_url: string
}

interface BaseProcessingFields {
  type: 'processing'
  text?: string
}

export interface BaseChatProcessingContent extends BaseProcessingFields {}

export interface ReviseDesignProcessingContent extends BaseProcessingFields {
  tool: 'revise_design'
  width: number
  height: number
  count: number
}

export type ChatProcessingContent = BaseChatProcessingContent | ReviseDesignProcessingContent

export interface ChatCaptionsContent {
  type: 'captions'
  content: string[]
  design_id: string
}

export interface ChatDesignPreviewContent {
  type: 'design_content'
  content: Array<StoreSnapshot<any>>
  width: number
  height: number
  design_id: string
}

export type ChatUnsupportedRequestNavigation = 'design' | 'brand_kit_styles' | 'brand_kit_content_preferences'

export interface ChatUnsupportedRequestContent {
  type: 'unsupported_request'
  navigation?: ChatUnsupportedRequestNavigation[]
  design_id?: string
}

export interface DesignerChatContext {
  design_id: string
}

export type ChatContextItem = DesignerChatContext

export interface ChatContextContent {
  type: 'context'
  context: ChatContextItem
}

export interface ChatRatingContent {
  type: 'rated'
  rating: 'upvote' | 'downvote'
}

export type ChatMessageContent =
  | ChatTextContent
  | ChatImageContent
  | ChatProcessingContent
  | ChatContextContent
  | ChatDesignPreviewContent
  | ChatCaptionsContent
  | ChatRatingContent
  | ChatUnsupportedRequestContent

export interface ChatMessage {
  id: number
  thread_id: number
  role: ChatMessageRole
  content: Array<ChatMessageContent>
  created_at: string
  updated_at: string
  status?: AIJobStatus
}

export interface ChatThread {
  id: number
  name: string | null
  messages: ChatMessage[]
  created_at: string
  updated_at: string
  original_design_content?: any
  original_caption?: string
}

export interface PaginatedChatParams {
  page: number
  items: number
}

export const ChatClient = {
  createThread: async (workspaceId: number, name?: string, content?: ChatMessageContent[], fileId?: string) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads`, {
      method: 'post',
      body: { name, content, almanac_file_id: fileId },
    })

    return res
  },

  getThread: async (workspaceId: number, id: number) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads/${id}`, {
      method: 'get',
    })

    return res
  },

  updateThread: async (workspaceId: number, id: number, updatedThread: Partial<ChatThread>) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads/${id}`, {
      method: 'put',
      body: updatedThread,
    })

    return res
  },

  deleteThread: async (workspaceId: number, id: number) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/threads/${id}`, {
      method: 'delete',
    })

    return res
  },

  createMessage: async (workspaceId: number, threadId: number, content: ChatMessageContent[]) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(`/w/${workspaceId}/threads/${threadId}/messages`, {
      method: 'post',
      body: { content },
    })

    return res
  },

  retryMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(
      `/w/${workspaceId}/threads/${threadId}/messages/${messageId}/retry`,
      {
        method: 'post',
      },
    )

    return res
  },

  abortMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(
      `/w/${workspaceId}/threads/${threadId}/messages/${messageId}/abort`,
      {
        method: 'post',
      },
    )

    return res
  },

  deleteMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/threads/${threadId}/messages/${messageId}`, {
      method: 'delete',
    })

    return res
  },

  rateMessage: (messageId: number, rating: 'downvote' | 'upvote') =>
    sendToApi(`/rate_chat_message`, {
      method: 'POST',
      body: { chat_message_id: messageId, rating },
    }),
}
